import { useCallback, useEffect, useMemo, useRef } from 'react'
import { Box, Button, Flex, Image, Text, Textarea } from '@chakra-ui/react'
import { AnimatePresence, motion } from 'framer-motion'

import { H2 } from '@/components/ui/Headings'
import IconClose from '@/components/icons/IconClose'
import IconSparkle from '@/components/icons/IconSparkle'
import { useBrandContext } from '@/context/brand-context'
import { useContentModeration } from '@/hooks/use-content-moderation'
import { containsSensitiveContent } from '@/utils/check-sensitive-content'
import { RETAIL_EVENTS } from '@/views/onboarding/constants/retail-events'
import '@/components/ambient/animations.css'

import type { GraphicItem, CreativityLevel } from '@/context/brand-context'

const MAX_GRAPHICS = 3

const SAMPLE_GRAPHICS = [
  { url: '/samples/bugs.jpg', name: 'Bugs Bunny' },
  { url: '/samples/scooby.png', name: 'Scooby Doo' },
  { url: '/samples/superman.png', name: 'Superman' }
]

const CREATIVITY_OPTIONS: {
  value: CreativityLevel
  label: string
  description: string
}[] = [
  {
    value: 'protected',
    label: 'IP Protected',
    description:
      'Your uploaded graphics are fully protected and incorporated faithfully'
  },
  {
    value: 'balanced',
    label: 'Be Balanced',
    description:
      'A creative blend that respects your brand while exploring new territory'
  },
  {
    value: 'wild',
    label: 'Go Crazy',
    description:
      'Maximum creative freedom — expect bold, unexpected, and wildly original designs'
  }
]


const staggerContainer = {
  animate: { transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
}

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const }
  }
}

interface UploadGraphicsStepProps {
  onBack?: () => void
  onGenerate: () => void
}

export default function UploadGraphicsStep({ onBack, onGenerate }: UploadGraphicsStepProps) {
  const {
    brandAnalysis,
    selectedGraphics,
    setSelectedGraphics,
    designPrompt,
    setDesignPrompt,
    creativityLevel,
    setCreativityLevel,
    selectedRetailEvent,
    setSelectedRetailEvent
  } = useBrandContext()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const samplesLoaded = useRef(false)

  const mergedEvents = useMemo(() => {
    const suggested = brandAnalysis?.sentiment.suggestedRetailEvents ?? []
    const suggestedLower = new Set(suggested.map((e) => e.toLowerCase()))
    const hardcoded = RETAIL_EVENTS.filter(
      (e) => !suggestedLower.has(e.toLowerCase())
    )
    return [
      ...suggested.map((e) => ({ name: e, isAI: true })),
      ...hardcoded.map((e) => ({ name: e, isAI: false }))
    ]
  }, [brandAnalysis?.sentiment.suggestedRetailEvents])

  const handleEventSelect = useCallback(
    (event: string | null) => {
      setSelectedRetailEvent(selectedRetailEvent === event ? null : event)
    },
    [selectedRetailEvent, setSelectedRetailEvent]
  )

  useEffect(() => {
    if (samplesLoaded.current || selectedGraphics.length > 0) return
    samplesLoaded.current = true

    const loadSamples = async () => {
      const items: GraphicItem[] = await Promise.all(
        SAMPLE_GRAPHICS.map(async (sample) => {
          const response = await fetch(sample.url)
          const blob = await response.blob()
          const ext = sample.url.split('.').pop() ?? 'jpg'
          const file = new File([blob], `${sample.name}.${ext}`, { type: blob.type })
          return {
            type: 'uploaded' as const,
            url: URL.createObjectURL(file),
            name: sample.name,
            file
          }
        })
      )
      setSelectedGraphics(items)
    }

    loadSamples()
  }, [selectedGraphics.length, setSelectedGraphics])

  const handleFileUpload = useCallback(
    (files: FileList | null) => {
      if (!files) return

      const newItems: GraphicItem[] = Array.from(files)
        .slice(0, MAX_GRAPHICS)
        .map((file) => ({
          type: 'uploaded' as const,
          url: URL.createObjectURL(file),
          name: file.name,
          file
        }))

      selectedGraphics.forEach((g) => {
        if (g.url.startsWith('blob:')) URL.revokeObjectURL(g.url)
      })
      setSelectedGraphics(newItems)
    },
    [selectedGraphics, setSelectedGraphics]
  )

  const removeGraphic = (index: number) => {
    const updated = [...selectedGraphics]
    const removed = updated.splice(index, 1)[0]
    if (removed.url.startsWith('blob:')) {
      URL.revokeObjectURL(removed.url)
    }
    setSelectedGraphics(updated)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    handleFileUpload(e.dataTransfer.files)
  }

  const { isFlagged } = useContentModeration(designPrompt)

  const canGenerate =
    selectedGraphics.length > 0 && designPrompt.trim().length > 0 && !isFlagged

  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: '640px',
        margin: '0 auto',
        padding: '0 24px'
      }}
    >
      <motion.div variants={fadeInUp} style={{ marginBottom: '8px' }}>
        <H2 color="text.primary" textAlign="center" fontWeight={300}>
          Upload your graphics
        </H2>
      </motion.div>

      <motion.div variants={fadeInUp}>
        <Text color="white" textStyle="body" textAlign="center" mb="10px">
          Add up to {MAX_GRAPHICS} graphics and describe the direction
        </Text>
      </motion.div>

      {/* Upload zone */}
      <motion.div
        variants={fadeInUp}
        style={{ width: '100%', marginBottom: '8px' }}
      >
        <Box
          w="100%"
          h="88px"
          border="2px dashed"
          borderColor="border.subtle"
          borderRadius="16px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          cursor="pointer"
          transition="all 0.3s ease"
          className="animate-shimmer"
          _hover={{
            borderColor: 'accent.500',
            bg: 'rgba(0, 112, 243, 0.03)',
            boxShadow: '0 0 30px rgba(0, 112, 243, 0.08) inset'
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <Text color="white" textStyle="body" mb="4px">
            Drag & drop graphics here
          </Text>
          <Text color="whiteAlpha.700" textStyle="bodySuperSmall">
            or click to browse (PNG, JPG, SVG)
          </Text>
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/svg+xml,image/webp"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFileUpload(e.target.files)}
        />
      </motion.div>

      {/* Selected uploads */}
      {selectedGraphics.length > 0 && (
        <Flex gap="8px" mb="8px" flexWrap="wrap" justifyContent="center">
          <AnimatePresence>
            {selectedGraphics.map((graphic, index) => (
              <motion.div
                key={graphic.url}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
              >
                <Box
                  w="64px"
                  h="64px"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor="accent.500"
                  overflow="hidden"
                  position="relative"
                  bg="bg.secondary"
                >
                  <Image
                    src={graphic.url}
                    alt={graphic.name}
                    w="100%"
                    h="100%"
                    objectFit="cover"
                  />
                  <Box
                    position="absolute"
                    top="4px"
                    right="4px"
                    w="20px"
                    h="20px"
                    borderRadius="full"
                    bg="bg.primary"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    _hover={{ bg: 'negative' }}
                    onClick={(e) => {
                      e.stopPropagation()
                      removeGraphic(index)
                    }}
                  >
                    <IconClose boxSize="12px" color="text.primary" />
                  </Box>
                </Box>
              </motion.div>
            ))}
          </AnimatePresence>
        </Flex>
      )}

      {/* Selected count */}
      <motion.div variants={fadeInUp}>
        <Text color="white" textStyle="bodySuperSmall" mb="8px">
          {selectedGraphics.length} of {MAX_GRAPHICS} selected
        </Text>
      </motion.div>

      {/* Retail event selector */}
      <motion.div
        variants={fadeInUp}
        style={{ width: '100%', marginBottom: '8px' }}
      >
        <Text color="white" textStyle="label" mb="8px">
          Retail Event
        </Text>
        <Flex
          gap="8px"
          overflowX="auto"
          pb="4px"
          css={{
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {mergedEvents.map((event) => {
            const isSelected = selectedRetailEvent === event.name
            return (
              <Box
                key={event.name}
                as="button"
                flexShrink={0}
                display="flex"
                alignItems="center"
                gap="4px"
                borderRadius="20px"
                px="12px"
                py="6px"
                border="1px solid"
                bg={isSelected ? 'rgba(0, 112, 243, 0.12)' : 'bg.surface'}
                borderColor={
                  isSelected
                    ? 'accent.500'
                    : event.isAI
                      ? 'rgba(0, 112, 243, 0.15)'
                      : 'border.subtle'
                }
                color={isSelected ? 'accent.400' : 'text.secondary'}
                cursor="pointer"
                transition="all 0.2s ease"
                _hover={{
                  borderColor: isSelected ? 'accent.400' : 'border.strong',
                  bg: isSelected
                    ? 'rgba(0, 112, 243, 0.16)'
                    : 'rgba(255, 255, 255, 0.04)'
                }}
                onClick={() => handleEventSelect(event.name)}
              >
                {event.isAI && (
                  <IconSparkle
                    boxSize="10px"
                    color={isSelected ? 'accent.400' : 'accent.500'}
                  />
                )}
                <Text textStyle="bodySuperSmall" whiteSpace="nowrap">
                  {event.name}
                </Text>
              </Box>
            )
          })}
        </Flex>
      </motion.div>

      {/* Creativity level slider */}
      <motion.div
        variants={fadeInUp}
        style={{ width: '100%', marginBottom: '8px' }}
      >
        <Text color="white" textStyle="label" mb="10px">
          Creativity Level
        </Text>

        {(() => {
          const STEP_ACCENTS = ['#0070F3', '#7C5CFC', '#DB2777']
          const stepIndex = CREATIVITY_OPTIONS.findIndex(
            (o) => o.value === creativityLevel
          )
          const pct = stepIndex * 50
          const accent = STEP_ACCENTS[stepIndex]

          return (
            <Box
              borderRadius="16px"
              border="1px solid"
              borderColor="border.subtle"
              bg="rgba(255, 255, 255, 0.02)"
              p="16px 16px 12px"
            >
              {/* Track area */}
              <Box
                position="relative"
                w="100%"
                h="44px"
                cursor="pointer"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  const x = (e.clientX - rect.left) / rect.width
                  const idx = Math.round(x * 2)
                  setCreativityLevel(
                    CREATIVITY_OPTIONS[Math.min(2, Math.max(0, idx))].value
                  )
                }}
              >
                {/* Background track */}
                <Box
                  position="absolute"
                  top="50%"
                  left="0"
                  right="0"
                  transform="translateY(-50%)"
                  h="3px"
                  bg="rgba(255, 255, 255, 0.06)"
                  borderRadius="full"
                />

                {/* Filled track */}
                <Box
                  position="absolute"
                  top="50%"
                  left="0"
                  transform="translateY(-50%)"
                  h="3px"
                  borderRadius="full"
                  bg={accent}
                  w={`${pct}%`}
                  transition="width 0.5s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.5s ease"
                />

                {/* Step dots — always rendered, active hidden under thumb */}
                {CREATIVITY_OPTIONS.map((option, i) => {
                  const isPassed = i <= stepIndex

                  return (
                    <Box
                      key={option.value}
                      position="absolute"
                      top="50%"
                      left={`${i * 50}%`}
                      transform="translate(-50%, -50%)"
                      zIndex={1}
                    >
                      <Box
                        w="7px"
                        h="7px"
                        borderRadius="full"
                        bg={
                          isPassed
                            ? STEP_ACCENTS[i]
                            : 'rgba(255, 255, 255, 0.12)'
                        }
                        transition="background-color 0.4s ease"
                      />
                    </Box>
                  )
                })}

                {/* Thumb */}
                <Box
                  position="absolute"
                  top="50%"
                  left={`${pct}%`}
                  transform="translate(-50%, -50%)"
                  zIndex={3}
                  transition="left 0.5s cubic-bezier(0.4, 0, 0.2, 1)"
                >
                  <Box
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    bg={accent}
                    boxShadow={`0 0 0 3px #0A0A0F, 0 0 0 5.5px ${accent}30, 0 2px 8px rgba(0,0,0,0.3)`}
                    transition="all 0.5s ease"
                    _hover={{
                      boxShadow: `0 0 0 3px #0A0A0F, 0 0 0 6.5px ${accent}45, 0 2px 12px rgba(0,0,0,0.4)`
                    }}
                  />
                </Box>
              </Box>

              {/* Labels */}
              <Flex justifyContent="space-between" mt="2px">
                {CREATIVITY_OPTIONS.map((option, i) => {
                  const isActive = option.value === creativityLevel
                  return (
                    <Box
                      key={option.value}
                      w="33.33%"
                      textAlign={
                        i === 0 ? 'left' : i === 2 ? 'right' : 'center'
                      }
                      cursor="pointer"
                      onClick={() => setCreativityLevel(option.value)}
                    >
                      <Text
                        textStyle="bodySuperSmall"
                        color={isActive ? 'text.primary' : 'text.tertiary'}
                        transition="color 0.4s ease"
                      >
                        {option.label}
                      </Text>
                    </Box>
                  )
                })}
              </Flex>

              {/* Description */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={creativityLevel}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                >
                  <Text
                    textStyle="bodySuperSmall"
                    color="text.secondary"
                    textAlign="center"
                    mt="8px"
                  >
                    {
                      CREATIVITY_OPTIONS.find(
                        (o) => o.value === creativityLevel
                      )?.description
                    }
                  </Text>
                </motion.div>
              </AnimatePresence>
            </Box>
          )
        })()}
      </motion.div>

      {/* Design prompt */}
      <motion.div
        variants={fadeInUp}
        style={{ width: '100%', marginBottom: '10px' }}
      >
        <Text color="white" textStyle="label" mb="6px">
          Describe Your Designs
        </Text>
        <Textarea
          value={designPrompt}
          onChange={(e) => setDesignPrompt(e.target.value)}
          placeholder="Describe what your designs should look like..."
          bg="bg.secondary"
          border="1px solid"
          borderColor={isFlagged ? 'red.500' : 'border.subtle'}
          borderRadius="12px"
          color="text.primary"
          _placeholder={{ color: 'text.tertiary' }}
          _hover={{ borderColor: isFlagged ? 'red.500' : 'border.strong' }}
          _focus={{
            borderColor: isFlagged ? 'red.500' : 'accent.500',
            boxShadow: isFlagged
              ? '0 0 0 1px var(--chakra-colors-red-500)'
              : '0 0 0 1px var(--chakra-colors-accent-500)'
          }}
          transition="border-color 0.2s ease"
          resize="none"
          minH="56px"
          p="12px"
        />
        {isFlagged && (
          <Text textStyle="bodySuperSmall" color="red.400" mt="6px">
            Your prompt contains content that violates our usage policy.
          </Text>
        )}
      </motion.div>

      <Box h="80px" />

      <Box
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        zIndex={10}
        px="24px"
        pb={{ base: '16px', md: '20px' }}
        pt="12px"
        bg="linear-gradient(to top, rgba(10, 10, 15, 1) 60%, rgba(10, 10, 15, 0))"
      >
        <Flex gap="12px" justifyContent="center" w="100%" maxW="420px" mx="auto">
          {onBack && (
            <Button
              h="48px"
              minW="132px"
              textStyle="body"
              fontWeight={500}
              borderRadius="10px"
              bg="bg.surface"
              color="text.primary"
              border="1px solid"
              borderColor="border.subtle"
              _hover={{ bg: 'rgba(255, 255, 255, 0.06)', borderColor: 'border.strong' }}
              transition="all 0.3s ease"
              onClick={onBack}
            >
              Go Back
            </Button>
          )}
          <Button
            h="48px"
            flex="1"
            textStyle="body"
            fontWeight={500}
            letterSpacing="0.02em"
            borderRadius="10px"
            bg={canGenerate ? 'accent.500' : 'bg.surface'}
            color={canGenerate ? 'white' : 'text.tertiary'}
            cursor={canGenerate ? 'pointer' : 'not-allowed'}
            border="1px solid"
            borderColor={canGenerate ? 'accent.500' : 'border.subtle'}
            transition="all 0.3s ease"
            className={canGenerate ? 'animate-glow-pulse' : ''}
            _hover={
              canGenerate
                ? {
                    bg: 'accent.400',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)'
                  }
                : {}
            }
            onClick={canGenerate && !containsSensitiveContent(designPrompt) ? onGenerate : undefined}
          >
            Generate Designs
          </Button>
        </Flex>
      </Box>
    </motion.div>
  )
}
